/**
 * Lifecycle and item types (v2.2).
 * Primary object is Item. Priority is contextual (list membership + rank), not a lifecycle state.
 */

import type { ItemAltitude, AltitudeLevel, DedupeStatus, ItemDetailedState, ItemExitStatus, PlanningMaturity, ItemKnowledgeStatus, ItemConfidenceLevel } from './itemLifecycle';

// ============================================================================
// Item Language Types (for Comparability)
// ============================================================================

/**
 * Language type for items - determines how they should be framed for comparison.
 * Items of different types cannot be reliably compared without normalization.
 * 
 * From "Managing Priorities": You can't reliably prioritize apples vs bicycles 
 * unless you convert them into a common unit or keep them in separate buckets.
 */
export type ItemLanguageType = 
  | 'problem'       // Something is wrong that needs fixing
  | 'opportunity'   // Something we could gain
  | 'risk'          // Something that might go wrong
  | 'solution'      // A specific approach/implementation
  | 'commitment'    // An external promise we must keep
  | 'investment';   // A strategic bet for future value

/**
 * @deprecated Use ItemLanguageType. Kept for backward compatibility.
 */
export type CandidateLanguageType = ItemLanguageType;

/**
 * Meta-categories for classification
 * Helps with organizing and filtering items
 */
export type ItemMetaCategory = 
  | 'people'        // Who - hiring, training, team structure
  | 'things'        // What - products, features, systems
  | 'places'        // Where - locations, markets, channels
  | 'activities'    // How - processes, operations
  | 'information'   // What we know - research, data, insights
  | 'time';         // When - deadlines, windows, schedules

/**
 * Strategic bucket for portfolio-level grouping
 */
export type StrategicBucket = 
  | 'growth'        // New revenue, expansion, market share
  | 'commitments'   // Customer promises, contracts, SLAs
  | 'reliability'   // Tech debt, stability, performance
  | 'platform'      // Infrastructure, enablers, foundations
  | 'compliance';   // Legal, regulatory, security

// ============================================================================
// Item Stewardship (Routing and Ownership)
// ============================================================================

/**
 * Stewardship bucket IDs - predefined buckets that stewards can own
 * These represent functional areas where ideas can be routed
 */
export type StewardshipBucket = 
  | 'product'       // Product team ideas
  | 'engineering'   // Engineering/tech improvements
  | 'operations'    // Operational efficiency
  | 'marketing'     // Marketing and growth
  | 'sales'         // Sales enablement
  | 'support'       // Customer support improvements
  | 'hr'            // People and culture
  | 'finance'       // Financial and budgeting
  | 'legal'         // Legal and compliance
  | 'general';      // General/unassigned

/**
 * ItemStewardship - tracks routing and ownership of an item
 * 
 * A steward is responsible for shepherding an idea through to completion.
 * Org-wide stewards (triage) can route ideas to bucket-specific stewards.
 */
export interface ItemStewardship {
  /** ID of the user assigned as steward */
  assignedStewardId?: string;
  
  /** 
   * Bucket this item belongs to (e.g., "product", "engineering")
   * Used for routing and filtering
   */
  bucketId?: StewardshipBucket | string;
  
  /** Display name of the bucket */
  bucketName?: string;
  
  /** User who routed this item to the current steward/bucket */
  routedBy?: string;
  
  /** When the routing happened */
  routedAt?: Date;
  
  /** Why it was routed (optional context for the steward) */
  routingReason?: string;
  
  /** Previous steward ID (for tracking handoffs) */
  previousStewardId?: string;
  
  /** Whether this item needs triage (no steward assigned) */
  needsTriage?: boolean;
}

/**
 * Default stewardship for new items
 */
export const DEFAULT_ITEM_STEWARDSHIP: ItemStewardship = {
  needsTriage: true,
};

// Lifecycle state: only 'item'. Priority is contextual (list + rank), not a state.
export type LifecycleState = 'item';

/**
 * @deprecated lifecycle_stage is no longer a canonical concept.
 * Item state is expressed via exit_status (active/deferred/archived) and
 * computed readiness from named inputs.
 */
export type LifecycleStage = 'intake' | 'shaping' | 'ranking' | 'execution';

// Base Item (the root entity)
export interface Item {
  id: string;
  title: string;
  description?: string;
  lifecycle_state: LifecycleState;
  /** @deprecated No longer a canonical concept. Use exit_status for item state. */
  lifecycle_stage?: LifecycleStage;
  /**
   * Canonical item exit status.
   * active = in active consideration (default)
   * deferred = explicitly postponed
   * archived = removed from active consideration; restorable
   */
  exit_status?: ItemExitStatus;
  /**
   * Where this item sits on the Directional → Detailed planning continuum.
   * directional = rough signals only (default; appropriate for Priority Formation).
   * Detailed Planning should only begin after a priority is accepted.
   */
  planning_maturity?: PlanningMaturity;
  /** Epistemic status of the core facts behind this item. Migration 413. */
  knowledge_status?: ItemKnowledgeStatus;
  /** Overall confidence in this item's Directional Planning inputs. Migration 413. */
  confidence_level?: ItemConfidenceLevel;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  /**
   * Real user UUID behind createdBy, when known (DB: created_by_user_id).
   * itemsStore sets this directly at creation (fork/merge/create); kept
   * optional since demo/legacy items may not carry it.
   */
  created_by_user_id?: string;
  /** @deprecated Use shapingOwnerId — maps to DB shaping_owner_id. steward_id is empty in production. */
  stewardId?: string;
  source?: string;
  /**
   * How a person handed this item in (DB: submitted_via, migration 751).
   * 'idea_form' = Submit Idea; 'direct_entry' = Submit Idea's direct-entry
   * variant. NULL for every other create path (imports, planning, API). Drives
   * the "a new idea is waiting" notification to workspace admins.
   */
  submittedVia?: 'idea_form' | 'direct_entry' | null;
  /** Tags every item created by one import run (DB: import_batch_id, migration 751). */
  importBatchId?: string | null;
  tags: string[];
  /**
   * Legacy readiness mirror (DB: ready_for_ranking / shaping_completeness).
   * Historically ShapedItem-only, but itemsStore constructs and reads these
   * directly off plain Item (fork/merge/create, getUnreadyItems). Computed
   * via computeLegacyReadinessMirror() in lib/itemReadiness.ts.
   */
  readyForRanking?: boolean;
  shapingCompleteness?: number;
  
  // Stewardship and routing information
  stewardship?: ItemStewardship;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  metadata?: Record<string, any>;
  
  // Multi-tenancy
  workspace_id?: string;      // Workspace ID for multi-tenant filtering (DB column name)
  /**
   * Canonical in-memory alias for workspace_id. itemsStore constructs new
   * items with this camelCase field and reads it defensively alongside
   * workspace_id (dual-read during the in-memory/DB naming migration).
   */
  workspaceId?: string;

  // Collaboration space (personal | team | org). NULL = org-level (visible in
  // the org space by default). Set at creation time from the active space.
  spaceId?: string | null;

  /** Product module that created this item (migration 490). Default: prioritization. */
  originModule?: import('./originModule').OriginModule;

  // Organizational scoping
  orgUnitId?: string;         // Which org unit does this item belong to?
  orgUnitPath?: string[];     // Full path for nested org units
  /**
   * Where this item lives in the product hierarchy (product_nodes, migration
   * 719). Structural placement only — no gate weight, not an item relationship
   * (canon 12 §5.8). null/undefined = unplaced.
   */
  productNodeId?: string | null;
  
  // Queue routing
  /**
   * The queue (org unit) this item was submitted to.
   * References an OrgUnit with queueSettings.enabled = true
   */
  queueId?: string;
  
  /**
   * Registry-controlled work type classification (Canon Doc 21, migration 287).
   * Set during item creation or enrichment. Read-only once the item enters Planning.
   * Maps to items.work_type in the DB. No FK to execution_recipe_templates (allows
   * custom values and avoids cross-domain coupling).
   */
  work_type?: string | null;

  /**
   * Marketing launch classification (slug → workspace_launch_types.value).
   * Distinct from Workload Templates (shared-team hour templates).
   */
  launch_type?: string | null;

  /**
   * Financial investment classification (slug → workspace_investment_types.value).
   */
  investment_type?: string | null;

  /**
   * Stable business key from an upstream system (workspace-unique).
   * Import/upsert match key per Bulk Import API (`external_id`).
   * Distinct from entity_external_ids (per-connector sync fabric links).
   */
  external_id?: string | null;


  // === COMPARABILITY CLASSIFICATION ===
  // These fields help ensure items can be compared apples-to-apples

  /**
   * Classification State — the fourth orthogonal state machine on an Item.
   * Tracks the provenance and acceptance lifecycle of category + altitude.
   *
   * empty    = not yet classified
   * proposed = an agent has suggested; awaiting human confirmation
   * approved = human has confirmed (accepted a proposal or set directly)
   *
   * DB column: classification_status. Default: 'empty'.
   */
  classificationStatus?: import('./itemClassification').ClassificationStatus;

  /**
   * Creation provenance — shared authorship vocabulary (two axes):
   * source (human | ai | automated) × reviewStatus (none | human_reviewed).
   * Legacy compressed field creationProvenance kept for filters/compat.
   *
   * See: ./creationProvenance.ts, docs/1-canon/30-creation-provenance.md
   *
   * DB: creation_source, creation_review_status (605); creation_provenance (566).
   */
  creationSource?: import('./creationProvenance').CreationSource;
  creationReviewStatus?: import('./creationProvenance').CreationReviewStatus;
  /** @deprecated Prefer creationSource + creationReviewStatus. */
  creationProvenance?: import('./creationProvenance').CreationProvenance;

  /**
   * Language type / item category (DB: item_category).
   * For comparison, items should be normalized to a common type.
   */
  itemCategory?: ItemLanguageType;

  /**
   * How the work is expected to be executed in a human+AI organization.
   * Proposed by the ATC agent; accepted alongside category and altitude.
   * DB column: execution_profile.
   */
  executionProfile?: import('./itemClassification').ExecutionProfile;

  /**
   * Meta-category for classification
   */
  metaCategory?: ItemMetaCategory;
  
  /**
   * Strategic bucket for portfolio grouping
   */
  strategicBucket?: StrategicBucket;
  
  // === STRATEGIC GOVERNANCE ===
  /**
   * True when this item was seeded by the executive who tossed the ball.
   * Displayed as a "Strategic" badge in ranking views so participants can
   * see which items were nominated from above, while still ranking all items
   * on their merits.
   */
  isStrategicallyNominated?: boolean;

  /**
   * The cycle ID whose executive nominated this item.
   * Used to scope the badge display to the relevant cycle.
   */
  nominatingCycleId?: string;

  /**
   * Desired outcome - what we hope to achieve (book: "required for good items")
   */
  desiredOutcome?: string;
  
  /**
   * Beneficiary - who benefits from this (customer segment, users, etc.)
   */
  beneficiary?: string;

  /**
   * Evidence of success - how will we know this worked? CSV-importable
   * narrative field (see lib/importConfigs.ts, ItemImportExportModal).
   * Not yet part of the Supabase item write path — same status as
   * desiredOutcome/beneficiary above.
   */
  evidenceOfSuccess?: string;

  /**
   * Why now - why is this the right time to pursue this? CSV-importable
   * narrative field, sibling to evidenceOfSuccess.
   */
  whyNow?: string;

  /**
   * Obstacles - known blockers or risks. CSV-importable narrative field,
   * sibling to evidenceOfSuccess. Distinct from DesiredOutcome.obstacles
   * (a structured DesiredOutcomeObstacle[] on a different entity).
   */
  obstacles?: string;

  /**
   * Acceptance criteria - what conditions define this item as done/accepted.
   * Sibling to desiredOutcome; already tracked in itemsStore's
   * TRACKED_FIELDS provenance list and its item_description_updated event
   * mapping, but was missing from the type itself.
   */
  acceptanceCriteria?: string;

  // === CLARIFICATION WORKFLOW ===
  /**
   * Clarification requests for this candidate
   */
  clarificationRequests?: ClarificationRequest[];
  
  /**
   * Quick flag for pending clarifications (computed)
   */
  hasPendingClarifications?: boolean;
  
  // === ITEM FRAMING ===
  /**
   * Alternative frames for this candidate
   */
  frames?: ItemFrame[];
  
  /**
   * Which frame is currently canonical
   */
  canonicalFrameId?: string;
  
  // === DEDUPE AND HIERARCHY ===
  /**
   * @deprecated Use altitudeLevel instead
   */
  altitude?: ItemAltitude;
  
  // === LEVELING (Dimension-Derived Altitude) ===
  /**
   * Computed altitude level (1-5)
   * Derived from altitudeScopingInputs (time horizon, work size, impact surface)
   * This is the authoritative altitude used for prioritization
   */
  altitudeLevel?: AltitudeLevel;
  
  /**
   * @deprecated Use altitudeLevel
   */
  declaredAltitude?: ItemAltitude;
  
  /**
   * @deprecated Use altitudeLevel
   */
  canonicalAltitude?: ItemAltitude;
  
  /**
   * Lifecycle state for altitude assignment (proposed -> under_review -> accepted)
   */
  altitudeLifecycleState?: 'proposed' | 'under_review' | 'accepted';
  
  /**
   * Scoping inputs that determine altitude
   * (time horizon, work size, impact surface)
   * Altitude level is computed from these three dimensions.
   */
  altitudeScopingInputs?: {
    timeHorizon?: 'days' | 'weeks' | '1-3_months' | '3-12_months' | '1+_years';
    workSize?: '1-2_weeks' | '3-8_weeks' | '9-26_weeks' | '27-52_weeks' | '52+_weeks';
    impactSurface?: 'one_screen' | 'one_workflow' | 'multiple_workflows' | 'multiple_products' | 'company_wide';
  };

  /**
   * Item Effort in capacity units (CU ≈ 8h) for viability planning.
   * Distinct from altitude workSize and from session scoring attribute `effort`.
   */
  effortCapacityUnits?: number | null;

  /**
   * Provenance for effortCapacityUnits.
   * `altitude_level_inferred` = sized from altitude 1–5 without an intentional workSize answer.
   */
  effortSource?:
    | 'explicit'
    | 'planner_override'
    | 'work_size'
    | 'altitude_level_inferred'
    | null;
  
  /**
   * Optional frame to use for altitude questions. When set, altitude questions
   * are adapted for this frame (e.g. Risk) instead of the item category. When unset,
   * questions use the item category. Enables "answer altitude as Risk" for an
   * item whose primary category is Opportunity.
   */
  altitudeFrame?: ItemLanguageType;
  
  /**
   * Completion requirements for high-altitude items (Levels 4-5)
   * At least one must be provided before item is complete
   */
  altitudeCompletionRequirements?: {
    successDefinition?: string;
    firstDeliverable?: string;
    owner?: string;
  };
  
  /**
   * Parent item ID for hierarchical relationships
   */
  parentItemId?: string;
  
  /**
   * Child item IDs for hierarchical relationships
   */
  childItemIds?: string[];
  
  // === LEADERSHIP APPROVAL (Priorities) ===
  /**
   * Whether this item has been approved in published-list context
   * Only items included in published lists with leadership approval appear in the Priorities view
   */
  leadershipApproved?: boolean;
  
  /**
   * When leadership approved this item
   */
  leadershipApprovedAt?: Date;
  
  /**
   * Who approved this item (leadership member ID)
   */
  leadershipApprovedBy?: string;
  
  /**
   * Dedupe status - whether this item has been checked for duplicates
   */
  dedupeStatus?: DedupeStatus;
  
  /**
   * If dedupeStatus is 'duplicate_of', this is the ID of the canonical item
   */
  duplicateOfId?: string;
  
  /**
   * If dedupeStatus is 'merged', these are the IDs of items that were merged into this one
   */
  mergedFromIds?: string[];

  /**
   * If this item was created by splitting another, the source item's ID
   */
  splitFromId?: string;
  
  // === LEGACY STATE FIELDS (deprecated) ===
  /**
   * @deprecated Use exit_status instead.
   * Kept for backward compat during consumer sweep.
   */
  detailedState?: ItemDetailedState;
  
  /**
   * Assigned item owner — the person responsible for enriching and shepherding this item.
   * Maps to DB column shaping_owner_id. Used by the authorization system for assignee-based access.
   */
  shapingOwnerId?: string;

  shapingStartedAt?: Date;

  shapedAt?: Date;
  
  // === OST (OPPORTUNITY SOLUTION TREE) INTEGRATION ===
  /**
   * If this candidate originated from an OST solution, this is the node ID
   */
  ostNodeId?: string;
  
  /**
   * The parent opportunity in the OST providing strategic context
   */
  ostOpportunityId?: string;

  // === PROMOTION PROVENANCE (Stage 3) ===
  /** Promotion event that created/updated this item (FK to promotion_events.id). */
  promotionEventId?: string | null;
  /** Cohort id for this promotion batch (shared by items from same Promotion Event). */
  promotionCohortId?: string | null;
  /** Session whose output was promoted to create/update this item. */
  sourceSessionId?: string | null;
  /** User id who performed the promotion. */
  promotedBy?: string | null;
  /** When the item was promoted. */
  promotedAt?: Date | null;

  // === DEFERRAL METADATA ===
  /**
   * When this item was deferred. Set when exit_status transitions to 'deferred'.
   * DB column: deferred_at
   */
  deferredAt?: Date | null;

  /**
   * The date on which a deferred item should be reviewed for reactivation or archival.
   * Set alongside exit_status = 'deferred'. DB column: defer_review_date
   */
  deferReviewDate?: Date | null;

  /** Why it was deferred, written when it was. DB column: defer_reason */
  deferReason?: string | null;

  // === OWNERSHIP ===
  /** The canonical owner of this item (user id). Defaults to createdBy on creation. */
  ownerId?: string | null;
  /**
   * When ownershipMode is 'delegated', the user id acting on behalf of the owner.
   * Null when ownershipMode is 'direct'.
   */
  delegateId?: string | null;
  /** 'direct' = owner is working the item; 'delegated' = owner has delegated to delegateId. */
  ownershipMode?: 'direct' | 'delegated';
}

// All work objects are Items from creation. No pre-Item stage.

// Attribute scope - determines where an attribute is visible/usable
export type AttributeScope = 
  | 'global'        // Organization-wide, all org units can use
  | 'department'    // Available to a specific department and its children
  | 'team'          // Available only to a specific team
  | 'process';      // Available only within a specific process

// ============================================================================
// Dial Point Definitions (for consistent value scales)
// ============================================================================

/**
 * Threshold condition for multi-state transitions
 * Defines when a state transition should occur
 */
export interface ThresholdCondition {
  type: 'field_value' | 'time_elapsed' | 'approval_count' | 'computed_score' | 'external_trigger';
  field?: string;              // Which field to check
  operator: 'gte' | 'lte' | 'eq' | 'between';
  value: number | string | [number, number];
}

/**
 * Side effect that occurs when entering a state
 */
export interface StateSideEffect {
  type: 'lock_fields' | 'notify' | 'require_approval' | 'trigger_workflow';
  config: Record<string, unknown>;
}

/**
 * State definition for multi-state attributes
 * Defines discrete states with threshold-driven transitions
 */
export interface StateDefinition {
  value: number;              // Ordinal position (1, 2, 3...)
  label: string;              // e.g., "Draft", "Ready", "Locked"
  description: string;        // What this state means
  entryThreshold?: {
    condition: ThresholdCondition;
    description: string;      // Human-readable explanation
  };
  sideEffects?: StateSideEffect[];
}

/**
 * A dial point defines a specific value on an attribute's scale.
 * This provides consistent interpretation across evaluators.
 * 
 * From "Managing Priorities": Good items include attributes with values 
 * that can be judged relative to each other.
 */
export interface DialPoint {
  value: number;              // Numeric value (0-3 typically)
  label: string;              // Display label (e.g., "High", "Medium", "Low")
  description: string;        // What this value means
  examples?: string[];        // Concrete examples to help evaluators
  threshold?: {
    type: 'requires_approval' | 'escalation' | 'decomposition_recommended';
    message: string;
  };
}

/**
 * Attribute display type - toggle (binary), dial (spectrum), or multiState (discrete ordered states)
 */
export type AttributeDisplayType = 'toggle' | 'dial' | 'multiState';

/**
 * Attribute semantic type - what kind of information this attribute represents
 */
export type AttributeSemanticType = 
  | 'descriptive'   // Context about the item (Category, Theme, Source)
  | 'evaluative'    // Used for scoring/ranking (Impact, Effort, Risk)
  | 'ownership'     // Who's responsible (Steward, Execution Owner)
  | 'temporal'      // Time-related (Due Date, Target Quarter)
  | 'evidence';     // Supporting rationale (Confidence, Source Reference)

/**
 * Attribute proposer type - who can set values for this attribute
 */
export type AttributeProposerType =
  | 'submitter'     // Original idea author
  | 'assignee'      // Shaping owner / assignee
  | 'specialist'    // Domain expert (e.g., Engineering for effort)
  | 'group'         // Requires group consensus
  | 'session_runner'; // Set during session

// ============================================================================
// Measurement Scales
// ============================================================================

/**
 * A measurement scale defines how an attribute's values are captured.
 * A single attribute can have many scales (e.g., Impact can be measured as
 * 1-5, Low/Medium/High, T-shirt sizes, etc.). Different contexts in the
 * global > cycle > track > session hierarchy can select which scale to use.
 */
export interface MeasurementScale {
  id: string;
  label: string;
  description?: string;
  scaleType: MeasurementScaleType;
  /** Ordered set of allowed values. For numeric ranges these are generated from min/max/step. */
  points: MeasurementPoint[];
  /** For numeric scales: lower bound */
  min?: number;
  /** For numeric scales: upper bound */
  max?: number;
  /** For numeric scales: increment (default 1) */
  step?: number;
  /** Whether this is the default scale for the attribute at global scope */
  isDefault?: boolean;
}

export type MeasurementScaleType =
  | 'numeric'       // 1-5, 1-10, 0-100, etc.
  | 'ordinal'       // Low/Medium/High, XS/S/M/L/XL, etc.
  | 'binary'        // Yes/No, Pass/Fail
  | 'percentage'    // 0-100%
  | 'currency'      // Dollar amounts
  | 'fibonacci'     // 1,2,3,5,8,13,21
  | 'custom';       // Arbitrary labeled values

export interface MeasurementPoint {
  value: number;
  label: string;
  description?: string;
  color?: string;
}

/**
 * Identifies which scale is selected for an attribute at a given level
 * of the global > cycle > track > session hierarchy.
 */
export interface AttributeScaleOverride {
  attributeKey: string;
  scaleId: string;
  /** Where this override applies */
  scope: 'global' | 'cycle' | 'track' | 'session';
  scopeId?: string;
}

// Attribute definition
export interface Attribute {
  /** Set by DB when table uses UUID primary key (e.g. Supabase). Required for update/delete when present. */
  id?: string;
  /**
   * True when this definition was read from the shared row every workspace inherits
   * (workspace_id null) because the workspace has none of its own. Saving it gives the
   * workspace its own copy instead of changing the shared row (migration 949).
   */
  inheritedFromShared?: boolean;
  /**
   * Set when this attribute stands for a strategic objective used as a criterion
   * (attributes.objective_id, migration 1066). Its label follows the objective and
   * its one scale is the item's link strength; see src/lib/objectiveCriteria.ts.
   */
  objectiveId?: string;
  key: string;
  label: string;
  /**
   * What this value means when a participant is scoring it. Required for system values;
   * strongly encouraged for custom values. Shown in-session so everyone is aligned on
   * the same definition before scoring. Values only become criteria when used for
   * comparison/ranking — the definition travels with them into that role.
   */
  definition?: string;
  /** True for the 6 values that ship with the product. System values cannot be edited or deleted. */
  isSystem?: boolean;
  type: 'text' | 'number' | 'select' | 'multiselect' | 'date' | 'boolean';
  required: boolean;
  options?: string[]; // For select/multiselect (legacy; prefer scales)
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };

  // === OWNERSHIP ===
  /** User ID of the person who owns/maintains this attribute definition */
  ownerId?: string;
  /** Display name cached for UI (denormalized from users table) */
  ownerName?: string;

  // === MEASUREMENT SCALES ===
  /** Multiple measurement scales this attribute can use. */
  scales?: MeasurementScale[];
  /** ID of the default scale at global level (references scales[].id) */
  defaultScaleId?: string;

  // === TYPE CLASSIFICATION ===
  /** Semantic type - what kind of information this attribute represents */
  semanticType?: AttributeSemanticType;
  
  /** Display type - toggle (binary), dial (spectrum), or multiState (discrete ordered) */
  displayType?: AttributeDisplayType;
  
  /** Proposer type - who can set values for this attribute */
  proposerType?: AttributeProposerType;
  
  /** Governance rules for this attribute (who can propose/approve values) */
  governance?: AttributeGovernance;
  
  // === DISPLAY-SPECIFIC CONFIG ===
  /** Configuration for dial-type attributes */
  dialConfig?: {
    min: number;
    max: number;
    dialPoints: DialPoint[];
  };
  
  /** Configuration for multiState-type attributes */
  multiStateConfig?: {
    states: StateDefinition[];
    transitionMode: 'threshold' | 'manual' | 'computed';
    sourceAttribute?: string;  // If computed from another dial
  };
  
  /** Dial point definitions for consistent value interpretation (legacy support) */
  dialPoints?: DialPoint[];
  
  /** Proposer role(s) - who can propose values for this attribute */
  proposerRoles?: string[];
  
  // === ALTITUDE SCOPING ===
  /** Which altitudes this attribute applies to */
  altitudeRange?: {
    min: ItemAltitude;
    max: ItemAltitude;
  };
  
  /** Context scope - where this attribute is visible */
  contextScope?: 'global' | 'process' | 'session';
  
  /** At which states is this attribute required */
  requiredAt?: ItemDetailedState[];
  
  // === VISIBILITY SCOPING ===
  // Scoping - controls where this attribute is visible/usable
  scope: AttributeScope;
  scopeOrgUnitId?: string;    // For department/team scopes - which org unit owns it
  scopeProcessId?: string;    // For process scope - which process owns it
  
  // Inheritance
  inheritable: boolean;       // Can child scopes use this attribute?
  overridable: boolean;       // Can child scopes override this attribute's options?
  
  // Metadata
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  category?: string;          // For grouping in UI (e.g., "Technical", "Business", "Risk")
  description?: string;       // Help text for users
  
  // === EVIDENCE REQUIREMENTS (WS2) ===
  /** Evidence requirement level for this attribute */
  evidenceRequirement?: 'optional' | 'recommended' | 'required';
  
  /** Acceptable evidence types for this attribute */
  acceptableEvidenceTypes?: Array<
    'data' | 'research' | 'expert_opinion' | 'customer_feedback' | 
    'market_signal' | 'competitor_intel' | 'internal_observation' | 'assumption'
  >;
  
  /** Minimum reliability level for evidence on this attribute */
  minimumEvidenceReliability?: 'verified' | 'corroborated' | 'single_source' | 'anecdotal' | 'speculative';
  
  /** Minimum number of evidence items required */
  minimumEvidenceCount?: number;
  
  /** Block value proposals without sufficient evidence */
  blockWithoutEvidence?: boolean;

  // === CUSTOM FIELDS ===
  /**
   * When true, this attribute is a general-purpose custom field on the item record
   * (e.g. "Launch Date", "Customer Segment"), distinct from scoring/evaluation attributes
   * used in prioritization sessions.
   */
  isCustomField?: boolean;

  /**
   * Whether this custom field is currently active and visible on item detail views.
   * Only meaningful when isCustomField is true. Defaults to true when not set.
   */
  enabled?: boolean;

  /**
   * Whether this custom field may be selected as a scoring criterion in comparison
   * frames, sessions, workflows, and templates.
   *
   * Only meaningful when isCustomField is true. Defaults to false — custom fields
   * are data capture fields (free text, dates, dropdowns for things like "Division"
   * or "Comments") and should NOT appear in criteria pickers unless a workspace admin
   * explicitly opts them in. System attributes (isCustomField !== true) are always
   * criterion-eligible; this flag is ignored for them.
   */
  usable_as_criterion?: boolean;

  /**
   * The object type this custom field is attached to (e.g. 'item', 'cycle', 'session').
   * Only meaningful when isCustomField is true. Defaults to 'item' when not set.
   * Future: multiple object types will be supported.
   */
  objectType?: string;

  /**
   * Where an item custom field shows (owner ruling 2026-10-15). Unset means
   * today's place: the item page's Assessment tab only. Child fields show with
   * their parent and ignore this. See src/lib/customFieldPlacement.ts.
   */
  placement?: CustomFieldPlacement;

  /**
   * How this custom field is rendered in item detail views.
   *
   *  cards      — altitude-style: each option is a tappable card (select / multiselect, 3–8 options)
   *  pills      — compact chip buttons in a row (multiselect, many options)
   *  dropdown   — standard <select> dropdown (select, many options)
   *  radio      — radio buttons laid out horizontally (select, 2–5 options)
   *  toggle     — yes / no toggle (boolean only)
   *  text-input — free-form single-line text input
   *  number-input — numeric input
   *  date-picker  — calendar date picker
   *
   * When omitted the renderer picks a sensible default based on field.type and
   * the number of options.
   */
  customDisplayType?: CustomFieldDisplayType;

  /**
   * Key of a sibling custom field whose value gates the options shown in this field.
   * The parent field must be type 'select'. When a parent is set, this field is
   * hidden until the parent has a value, and only the relevant subset of options is shown.
   *
   * Example: parentKey: 'division' → this "team" field only appears after Division is chosen,
   * and shows only the teams that belong to that division.
   */
  parentKey?: string;

  /**
   * Maps each possible parent field value to the subset of options valid for this field.
   * Only meaningful when parentKey is set.
   *
   * Example:
   *   { "Engineering": ["Frontend", "Backend", "Mobile"], "Design": ["UX", "Visual"] }
   */
  conditionalOptions?: Record<string, string[]>;
}

/**
 * How a custom field is rendered in the UI.
 * See the customDisplayType field on Attribute for full descriptions.
 */
/** An item page tab a custom field can show on, or none. */
export type CustomFieldPlacementTab = 'overview' | 'assessment' | 'connections' | 'prioritization' | 'delivery' | 'none';

/** Where an item custom field shows: one item page tab, and any of three other spots. */
export interface CustomFieldPlacement {
  tab: CustomFieldPlacementTab;
  /** The item page's right-hand column, "At a glance". */
  glance: boolean;
  /** The quick panel that opens from a list row. */
  quickPanel: boolean;
  /** Shown as a column in the item list unless a person has chosen their own columns. */
  listColumn: boolean;
}

export type CustomFieldDisplayType =
  | 'cards'
  | 'pills'
  | 'dropdown'
  | 'radio'
  | 'toggle'
  | 'text-input'
  | 'number-input'
  | 'date-picker';

// ============================================================================
// Standard Attributes (Book-Recommended)
// ============================================================================

/**
 * Standard dial points for Low/Medium/High attributes
 */
export const STANDARD_DIAL_POINTS: DialPoint[] = [
  { value: 0, label: 'None/N/A', description: 'Not applicable or no measurable value' },
  { value: 1, label: 'Low', description: 'Minimal impact or effort' },
  { value: 2, label: 'Medium', description: 'Moderate impact or effort' },
  { value: 3, label: 'High', description: 'Significant impact or effort' },
];

/**
 * Book-aligned dial points using "sufficiency" framing
 */
export const SUFFICIENCY_DIAL_POINTS: DialPoint[] = [
  { value: 0, label: 'Insufficient', description: 'Does not meet minimum threshold' },
  { value: 1, label: 'Barely Sufficient', description: 'Meets minimum threshold' },
  { value: 2, label: 'Sufficient', description: 'Meets expectations' },
  { value: 3, label: 'Ideal', description: 'Exceeds expectations significantly' },
];

// ============================================================================
// Enhanced Dial Point Definitions (Phase 6)
// ============================================================================

/**
 * Impact dial points with examples
 */
export const IMPACT_DIAL_POINTS: DialPoint[] = [
  { 
    value: 1, 
    label: 'Minimal', 
    description: 'Minor improvement, affects few users',
    examples: ['Bug fix with workaround', 'Small UX tweak', 'Single-user optimization'],
  },
  { 
    value: 2, 
    label: 'Limited', 
    description: 'Noticeable improvement for a subset of users',
    examples: ['Feature enhancement', 'Performance improvement', 'New optional capability'],
  },
  { 
    value: 3, 
    label: 'Moderate', 
    description: 'Meaningful improvement across significant user base',
    examples: ['New workflow capability', 'Major UX improvement', 'Important integration'],
  },
  { 
    value: 4, 
    label: 'Significant', 
    description: 'Major improvement with measurable business impact',
    examples: ['New market capability', 'Competitive differentiator', 'Revenue driver'],
    threshold: { type: 'requires_approval', message: 'High impact items need stakeholder sign-off' },
  },
  { 
    value: 5, 
    label: 'Transformative', 
    description: 'Game-changing impact on business or market position',
    examples: ['New product line', 'Market expansion', 'Strategic pivot enabler'],
    threshold: { type: 'requires_approval', message: 'Transformative items require executive approval' },
  },
];

/**
 * Effort dial points with time anchors
 */
export const EFFORT_DIAL_POINTS: DialPoint[] = [
  { 
    value: 1, 
    label: 'Trivial', 
    description: 'Less than 1 day of work',
    examples: ['Config change', 'Minor text update', 'Simple bug fix'],
  },
  { 
    value: 2, 
    label: 'Small', 
    description: '1-3 days of work',
    examples: ['Simple feature', 'UI enhancement', 'Standard integration'],
  },
  { 
    value: 3, 
    label: 'Medium', 
    description: '1-2 weeks of work',
    examples: ['New component', 'Complex feature', 'Multi-system change'],
  },
  { 
    value: 4, 
    label: 'Large', 
    description: '2-4 weeks of work',
    examples: ['Major feature', 'Architectural change', 'Complex integration'],
    threshold: { type: 'decomposition_recommended', message: 'Consider breaking this into smaller items' },
  },
  { 
    value: 5, 
    label: 'Extra Large', 
    description: '1+ month of work',
    examples: ['Platform overhaul', 'New system', 'Major migration'],
    threshold: { type: 'decomposition_recommended', message: 'This should be decomposed into epics/features' },
  },
];

/**
 * Risk dial points with response expectations
 */
export const RISK_DIAL_POINTS: DialPoint[] = [
  { 
    value: 1, 
    label: 'Negligible', 
    description: 'Risk is understood and minimal',
    examples: ['Well-tested pattern', 'Low-stakes change', 'Reversible decision'],
  },
  { 
    value: 2, 
    label: 'Low', 
    description: 'Some risk but manageable with standard practices',
    examples: ['New vendor', 'Minor scope uncertainty', 'Team stretch goal'],
  },
  { 
    value: 3, 
    label: 'Medium', 
    description: 'Material risk requiring active management',
    examples: ['New technology', 'Tight deadline', 'External dependency'],
  },
  { 
    value: 4, 
    label: 'High', 
    description: 'Significant risk with potential for major impact',
    examples: ['Unproven approach', 'Critical path dependency', 'Regulatory exposure'],
    threshold: { type: 'escalation', message: 'High risk items need mitigation plan before starting' },
  },
  { 
    value: 5, 
    label: 'Critical', 
    description: 'Existential risk to project or business outcomes',
    examples: ['Make-or-break bet', 'Single point of failure', 'Compliance requirement'],
    threshold: { type: 'escalation', message: 'Critical risk requires executive visibility and mitigation' },
  },
];

/**
 * Urgency dial points with time anchors
 */
export const URGENCY_DIAL_POINTS: DialPoint[] = [
  { 
    value: 1, 
    label: 'No deadline', 
    description: 'Can be done whenever capacity allows',
    examples: ['Nice-to-have improvement', 'Future consideration', 'Exploration'],
  },
  { 
    value: 2, 
    label: 'This quarter', 
    description: 'Should be addressed within the current quarter',
    examples: ['Quarterly commitment', 'Planning cycle item', 'Roadmap feature'],
  },
  { 
    value: 3, 
    label: 'This month', 
    description: 'Needs to be addressed within 30 days',
    examples: ['Sprint commitment', 'Customer promise', 'Release target'],
  },
  { 
    value: 4, 
    label: 'This week', 
    description: 'Needs attention within 7 days',
    examples: ['Urgent customer request', 'Critical bug', 'Blocking issue'],
    threshold: { type: 'escalation', message: 'Urgent items should be escalated to leadership' },
  },
  { 
    value: 5, 
    label: 'Immediate', 
    description: 'Needs immediate attention (drop everything)',
    examples: ['Production outage', 'Security incident', 'Critical customer escalation'],
    threshold: { type: 'escalation', message: 'Immediate priority requires all-hands response' },
  },
];

/**
 * Confidence dial points
 */
export const CONFIDENCE_DIAL_POINTS: DialPoint[] = [
  { 
    value: 1, 
    label: 'Speculative', 
    description: 'Based on assumptions, needs validation',
    examples: ['Initial estimate', 'First guess', 'No prior experience'],
  },
  { 
    value: 2, 
    label: 'Estimated', 
    description: 'Based on experience with similar work',
    examples: ['Comparable past work', 'Team consensus', 'Historical data'],
  },
  { 
    value: 3, 
    label: 'Validated', 
    description: 'Confirmed through research or prototype',
    examples: ['Spike completed', 'POC done', 'User research confirms'],
  },
  { 
    value: 4, 
    label: 'Committed', 
    description: 'High confidence based on detailed analysis',
    examples: ['Detailed breakdown', 'Team committed', 'Dependencies confirmed'],
  },
];

/**
 * Standard attribute keys that the system expects
 */
export const STANDARD_ATTRIBUTE_KEYS = {
  IMPACT: 'impact',
  EFFORT: 'effort',
  RISK: 'risk',
  URGENCY: 'urgency',
  CONFIDENCE: 'confidence',
  STRATEGIC_ALIGNMENT: 'strategic_alignment',
  COST_OF_DELAY: 'cost_of_delay',
} as const;

/**
 * Standard attributes recommended by the Managing Priorities framework.
 * These should exist in every workspace.
 * Enhanced with semantic types, proposer types, and detailed dial points.
 */
export const STANDARD_ATTRIBUTES: Partial<Attribute>[] = [
  {
    key: STANDARD_ATTRIBUTE_KEYS.IMPACT,
    label: 'Impact',
    type: 'select',
    required: true,
    options: ['Minimal', 'Limited', 'Moderate', 'Significant', 'Transformative'],
    semanticType: 'evaluative',
    displayType: 'dial',
    proposerType: 'group',
    dialPoints: IMPACT_DIAL_POINTS,
    dialConfig: { min: 1, max: 5, dialPoints: IMPACT_DIAL_POINTS },
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    contextScope: 'global',
    category: 'Value',
    description: 'The expected value or benefit if this item is delivered',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.EFFORT,
    label: 'Effort',
    type: 'select',
    required: true,
    options: ['Trivial', 'Small', 'Medium', 'Large', 'Extra Large'],
    semanticType: 'evaluative',
    displayType: 'dial',
    proposerType: 'specialist',
    dialPoints: EFFORT_DIAL_POINTS,
    dialConfig: { min: 1, max: 5, dialPoints: EFFORT_DIAL_POINTS },
    proposerRoles: ['engineer', 'tech-lead'],
    contextScope: 'global',
    category: 'Cost',
    description: 'The expected work required to deliver this item (1: <1d, 2: 1-3d, 3: 1-2w, 4: 2-4w, 5: 1m+)',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.RISK,
    label: 'Risk',
    type: 'select',
    required: false,
    options: ['Negligible', 'Low', 'Medium', 'High', 'Critical'],
    semanticType: 'evaluative',
    displayType: 'dial',
    proposerType: 'specialist',
    dialPoints: RISK_DIAL_POINTS,
    dialConfig: { min: 1, max: 5, dialPoints: RISK_DIAL_POINTS },
    proposerRoles: ['engineer', 'tech-lead', 'risk-owner'],
    contextScope: 'global',
    category: 'Risk',
    description: 'The likelihood and potential severity of complications or failure',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.URGENCY,
    label: 'Urgency',
    type: 'select',
    required: false,
    options: ['No deadline', 'This quarter', 'This month', 'This week', 'Immediate'],
    semanticType: 'temporal',
    displayType: 'dial',
    proposerType: 'assignee',
    dialPoints: URGENCY_DIAL_POINTS,
    dialConfig: { min: 1, max: 5, dialPoints: URGENCY_DIAL_POINTS },
    proposerRoles: ['product-owner', 'stakeholder', 'customer-success'],
    contextScope: 'global',
    category: 'Time',
    description: 'How time-sensitive is this item?',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.CONFIDENCE,
    label: 'Confidence',
    type: 'select',
    required: false,
    options: ['Speculative', 'Estimated', 'Validated', 'Committed'],
    semanticType: 'evidence',
    displayType: 'dial',
    proposerType: 'group',
    dialPoints: CONFIDENCE_DIAL_POINTS,
    dialConfig: { min: 1, max: 4, dialPoints: CONFIDENCE_DIAL_POINTS },
    proposerRoles: ['any'],
    contextScope: 'global',
    category: 'Quality',
    description: 'How confident are we in the other estimates?',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.STRATEGIC_ALIGNMENT,
    label: 'Strategic Alignment',
    type: 'select',
    required: false,
    options: ['None', 'Low', 'Medium', 'High'],
    semanticType: 'evaluative',
    displayType: 'dial',
    proposerType: 'assignee',
    dialPoints: STANDARD_DIAL_POINTS,
    proposerRoles: ['exec'],
    contextScope: 'global',
    category: 'Value',
    description: 'How well does this align with strategic goals?',
  },
  {
    key: STANDARD_ATTRIBUTE_KEYS.COST_OF_DELAY,
    label: 'Cost of Delay',
    type: 'select',
    required: false,
    options: ['None', 'Low', 'Medium', 'High', 'Severe'],
    semanticType: 'evaluative',
    displayType: 'dial',
    proposerType: 'assignee',
    dialPoints: [
      { value: 0, label: 'None', description: 'No cost to delaying', examples: ['Backlog item', 'Exploration'] },
      { value: 1, label: 'Low', description: 'Minor cost to delay', examples: ['Opportunity cost', 'Minor friction'] },
      { value: 2, label: 'Medium', description: 'Moderate cost to delay', examples: ['Competitive pressure', 'User churn'] },
      { value: 3, label: 'High', description: 'Significant cost to delay', examples: ['Revenue impact', 'Contract risk'] },
      { value: 4, label: 'Severe', description: 'Critical cost - delay is very costly', examples: ['Compliance deadline', 'Major customer loss'] },
    ],
    proposerRoles: ['product-owner', 'finance', 'exec'],
    category: 'Time',
    description: 'What is the cost of not doing this now?',
  },
];

// Attribute value
export interface AttributeValue {
  attributeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
  value: any;
  scoredValue?: number; // If used for scoring
}

// ShapedItem - an item with attributes and readiness signals. All rows have lifecycle_state 'item'.
export interface ShapedItem extends Item {
  lifecycle_state: 'item';
  /** @deprecated lifecycle_stage is no longer canonical. */
  lifecycle_stage?: LifecycleStage;
  attributes: AttributeValue[];
  shapingCompleteness: number; // 0-100% — legacy readiness signal, kept for backward compat
  readyForRanking: boolean;
}

// Priority - contextual: an item in a pool with rank (not a lifecycle state).
export interface Priority extends Omit<ShapedItem, 'lifecycle_state' | 'lifecycle_stage'> {
  lifecycle_state: 'item';
  /** @deprecated lifecycle_stage is no longer canonical. */
  lifecycle_stage?: LifecycleStage;
  itemPoolId: string; // DB: item_pool_id
  rank: number;
  aboveTheLine: boolean;
  slottingRationale?: string;
  scores?: Record<string, number>; // Scores by criteria
}

// Comparison Set
export interface ComparisonSet {
  id: string;
  name: string;
  description?: string;
  criteriaIds: string[]; // Which criteria apply
  includedItemIds: string[]; // Which items are in scope
  framing: string; // Framing statement
}

// ============================================================================
// Post-Publish Drift Types (for detecting changes after handoff)
// ============================================================================

export type PostPublishChangeType = 
  | 'priority_added'      // New priority added after publish
  | 'priority_removed'    // Priority removed after publish
  | 'priority_reordered'  // Rankings changed after publish
  | 'cutline_moved'       // Cut line position changed after publish
  | 'criteria_changed';   // Criteria weights or selection changed after publish

export interface PostPublishChange {
  id: string;
  type: PostPublishChangeType;
  detectedAt: Date;
  description: string;
  affectedPriorityIds?: string[];
  previousValue?: unknown;
  newValue?: unknown;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;
}

// ============================================================================
// Item Pool Status
// ============================================================================

/**
 * Item Pool status - the state machine for Item Pools in Catchball
 */
export type ItemPoolStatus =
  | 'draft'        // Being assembled or edited
  | 'synthesized'  // Compiled from sessions, internally coherent
  | 'proposed'     // Presented upward (middle → exec)
  | 'challenged'   // Returned with questions/concerns
  | 'accepted'     // Accepted as valid representation
  | 'incorporated' // Included in Priority List
  | 'superseded';  // Replaced by newer list

/**
 * Allowed state transitions for Item Pools
 */
export const ITEM_POOL_TRANSITIONS: Record<ItemPoolStatus, ItemPoolStatus[]> = {
  'draft': ['synthesized', 'superseded'],
  'synthesized': ['proposed', 'draft', 'superseded'],
  'proposed': ['challenged', 'accepted', 'superseded'],
  'challenged': ['proposed', 'draft', 'superseded'], // Can revise and re-propose
  'accepted': ['incorporated', 'superseded'],
  'incorporated': ['superseded'],
  'superseded': [],
};

/**
 * Check if an Item Pool state transition is allowed
 */
export function canTransitionItemPool(
  from: ItemPoolStatus,
  to: ItemPoolStatus
): boolean {
  return ITEM_POOL_TRANSITIONS[from].includes(to);
}

/**
 * Human-readable labels for Item Pool states
 */
export const ITEM_POOL_STATUS_LABELS: Record<ItemPoolStatus, string> = {
  'draft': 'Draft',
  'synthesized': 'Synthesized',
  'proposed': 'Proposed',
  'challenged': 'Challenged',
  'accepted': 'Accepted',
  'incorporated': 'Incorporated',
  'superseded': 'Superseded',
};

/**
 * Item Pool status badge colors
 */
export const ITEM_POOL_STATUS_COLORS: Record<ItemPoolStatus, string> = {
  'draft': 'bg-gray-100 text-gray-700',
  'synthesized': 'bg-blue-100 text-blue-700',
  'proposed': 'bg-amber-100 text-amber-700',
  'challenged': 'bg-red-100 text-red-700',
  'accepted': 'bg-green-100 text-green-700',
  'incorporated': 'bg-purple-100 text-purple-700',
  'superseded': 'bg-gray-100 text-gray-500',
};

// Item Pool
export interface ItemPool {
  id: string;
  name: string;
  description?: string;
  ownerId: string; // Pool Owner
  /**
   * Canonical in-memory workspace scoping field (DB column: workspace_id).
   * itemPoolsStore sets this at creation and reads it for policy checks
   * (falls back to the current workspace when a legacy pool has none).
   */
  workspaceId?: string;
  comparisonSetId: string;
  criteriaIds: string[];
  priorityIds: string[]; // Ordered array of priority IDs
  cutLinePosition: number; // Index in priorityIds array (items before are above the line)
  createdAt: Date;
  updatedAt: Date;
  lastRankedAt?: Date;
  /**
   * Item Pool state machine:
   * - draft: Being assembled or edited
   * - synthesized: Compiled from sessions, internally coherent
   * - proposed: Presented upward (middle → exec)
   * - challenged: Returned with questions/concerns
   * - accepted: Accepted as valid representation
   * - incorporated: Included in Priority List
   * - superseded: Replaced by newer list
   * 
   * Legacy states (mapped for backwards compatibility):
   * - active → synthesized
   * - published → incorporated
   * - archived → superseded
   */
  status: ItemPoolStatus;
  tags: string[];
  
  // === PUBLICATION TRACKING ===
  // When this item pool becomes the "Plan of Record" it gets published
  isPublished?: boolean;              // Defaults to false
  publishedAt?: Date;
  publishedBy?: string;
  version?: number;                   // Defaults to 0, increments on each publish
  
  // Snapshot of criteria weights at time of publication
  criteriaWeightsSnapshot?: Record<string, number>;
  
  // === DOWNSTREAM BOUNDARY (Planning) ===
  // Marks when priorities were handed off to planning systems
  handedOffToPlanningAt?: Date;
  handedOffToPlanningBy?: string;
  planningSystemRef?: string;         // External URL or ID of the plan
  
  // === POST-PUBLISH DRIFT DETECTION ===
  // Changes detected after publication that downstream systems should know about
  postPublishChanges?: PostPublishChange[];
  hasUnacknowledgedChanges?: boolean; // Quick flag for UI indicators
  
  // === WS5: ADVANCED VERSIONING ===
  // Full version history with snapshots
  versionHistory?: ItemPoolVersionEntry[];
  
  // Current working version (may differ from published version)
  workingVersion?: number;
  
  // Lock status - prevents edits when locked for publication review
  isLocked?: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  lockReason?: string;
  
  // === WS5: CASCADE TRACKING ===
  // Track which strategies and plans derive from this pool
  linkedStrategyIds?: string[];
  linkedPlanIds?: string[];
  
  // Cascade impact notifications
  cascadeNotificationsEnabled?: boolean;
  lastCascadeNotificationAt?: Date;
  
  // === WS5: PUBLICATION AUDIT TRAIL ===
  publicationHistory?: ItemPoolPublicationEvent[];
  
  // === TRACK SEMANTICS (v2.2) ===
  // Track Instance context: pool is scoped to a track within a cycle (DB: item_pools.track_id NOT NULL, cycle_id nullable).
  /**
   * Track Instance id. Required in DB; pools are always scoped to a track.
   */
  trackId: string;
  /**
   * Cycle id. Optional in DB; set when pool is used in a cycle context.
   */
  cycleId?: string;
  // When requiredItemCategory + requiredAltitudeLevel are set, the pool aligns to a Track Cell = (Item Category × Altitude)
  /**
   * Required item category for this pool. When set, all items must have this category.
   * Forms the horizontal axis of Track geometry.
   */
  requiredItemCategory?: string;
  
  /**
   * Required altitude level (1-5) for this pool. When set, all items must be at this altitude.
   * Forms the vertical axis of Track geometry.
   */
  requiredAltitudeLevel?: number;
  
  /**
   * When true, this pool represents a Track (must have requiredItemCategory and requiredAltitudeLevel set).
   * Tracks are the comparison lanes within a Cycle.
   */
  isTrack?: boolean;
  
  /**
   * Track governance state: active (in progress), accepted (ratified), finally_rejected (terminated).
   * Only meaningful when isTrack = true.
   */
  trackState?: 'active' | 'accepted' | 'finally_rejected';
  
  /**
   * Whether this pool is homogeneous (all items have same category and altitude).
   * Computed from membership; updated on membership changes.
   */
  isHomogeneous?: boolean;
  
  /**
   * When homogeneity was last validated for this pool.
   */
  homogeneityValidatedAt?: Date;
  
  /**
   * When pool membership was locked (required before S6 ranking).
   */
  poolLockedAt?: Date;
  
  /**
   * Who locked the pool.
   */
  poolLockedBy?: string;

  // === QUERY FEED (Large Sets PRD §4.3, migration 723) ===
  /** Saved query (saved_reports.id) this pool is fed from. Null/undefined = not fed. */
  feedQueryId?: string | null;
  /** Set to poolLockedAt by the DB when a fed pool locks; while set, new matches go to the Arrival Bay. */
  feedSeveredAt?: Date | null;
  /** When a human last accepted a "Refresh from feed" diff. */
  feedLastRefreshedAt?: Date | null;
}

/**
 * Version history entry for an Item Pool
 */
export interface ItemPoolVersionEntry {
  version: number;
  createdAt: Date;
  createdBy: string;
  
  // Snapshot data
  priorityIdsSnapshot: string[];
  cutLinePositionSnapshot: number;
  criteriaWeightsSnapshot: Record<string, number>;
  
  // Change summary
  changeSummary?: string;
  changesFromPrevious?: ItemPoolVersionChange[];
}

/**
 * Individual change in a version
 */
export interface ItemPoolVersionChange {
  type: 'priority_added' | 'priority_removed' | 'priority_reordered' | 'cutline_moved' | 'criteria_changed';
  description: string;
  affectedPriorityIds?: string[];
  previousValue?: unknown;
  newValue?: unknown;
}

/**
 * Publication event for audit trail
 */
export interface ItemPoolPublicationEvent {
  id: string;
  version: number;
  publishedAt: Date;
  publishedBy: string;
  
  // Context
  reason?: string;
  stakeholderApprovals?: Array<{
    userId: string;
    userName?: string;
    approvedAt: Date;
    role: string;
  }>;
  
  // Downstream impacts
  notifiedSystems?: string[];
  cascadedTo?: Array<{
    type: 'strategy' | 'plan';
    id: string;
    notifiedAt: Date;
  }>;
}

// Item Pool Map (collection of Item Pools)
export interface ItemPoolMap {
  id: string;
  name: string;
  description?: string;
  itemPoolIds: string[];
  dimensions: string[]; // Filtering dimensions (team, goal, time, etc.)
  createdAt: Date;
  updatedAt: Date;
}

// Drift Event (item_pool_id in DB)
export interface DriftEvent {
  id: string;
  /** Pool this drift applies to (DB: item_pool_id). Prefer itemPoolId. */
  itemPoolId: string;
  detectedAt: Date;
  type: 'below-line-work' | 'off-list-work' | 'stale-priority' | 'missing-work';
  severity: 'low' | 'medium' | 'high';
  description: string;
  affectedPriorityIds: string[];
  resolved: boolean;
  resolvedAt?: Date;
  resolutionNote?: string;
}

// Reprioritization Trigger
export interface ReprioritizationTrigger {
  id: string;
  name: string;
  description: string;
  condition: string; // Condition logic
  itemPoolId?: string; // If specific to a pool
  triggeredAt?: Date;
  acknowledged: boolean;
}

// Lifecycle transition validation
export interface TransitionValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  requiredActions?: string[];
}

// Shaping completeness check
export function calculateShapingCompleteness(
  item: Item,
  requiredAttributes: Attribute[]
): number {
  const shaped = item as ShapedItem;
  const attributeValues = shaped.attributes || [];
  
  const requiredFilled = requiredAttributes.filter(attr => 
    attr.required && attributeValues.some(av => av.attributeKey === attr.key && av.value)
  ).length;
  
  const totalRequired = requiredAttributes.filter(attr => attr.required).length;
  
  return totalRequired > 0 ? (requiredFilled / totalRequired) * 100 : 0;
}

// Validate promotion from Item to Priority (item in pool with rank)
export function validateItemToPriority(item: ShapedItem, itemPool: ItemPool): TransitionValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!item.readyForRanking) {
    errors.push('Item work is not complete');
  }
  
  if (item.shapingCompleteness < 80) {
    warnings.push(`Item work completeness is ${item.shapingCompleteness}% - consider completing more attributes`);
  }
  
  // Check if item has values for all required criteria
  const criteriaKeys = itemPool.criteriaIds;
  const missingCriteria = criteriaKeys.filter(key => 
    !item.attributes.some(attr => attr.attributeKey === key && attr.value !== undefined)
  );
  
  if (missingCriteria.length > 0) {
    errors.push(`Missing values for criteria: ${missingCriteria.join(', ')}`);
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

// ============================================================================
// CLARIFICATION WORKFLOW
// ============================================================================

/**
 * Status for a clarification request
 */
export type ClarificationStatus = 'open' | 'answered' | 'accepted' | 'withdrawn';

/**
 * Clarification request for an item
 * Tracks Q&A between shaper and submitter to remove ambiguity
 */
export interface ClarificationRequest {
  id: string;
  itemId: string;
  requestedBy: string;           // Who's asking
  requestedFrom: string;         // Who needs to answer (usually submitter)
  question: string;              // The specific question
  context?: string;              // Why this matters
  status: ClarificationStatus;
  createdAt: Date;
  
  // Response
  response?: string;
  respondedBy?: string;
  respondedAt?: Date;
  
  // Resolution
  acceptedBy?: string;
  acceptedAt?: Date;
  resolutionNote?: string;
}

// ============================================================================
// ITEM FRAMING
// ============================================================================

/**
 * Item frame - alternative expression of the same item
 * Allows the same item to be expressed as a problem, opportunity, etc.
 */
export interface ItemFrame {
  id: string;
  sourceItemId: string;            // The original item
  frameType: ItemLanguageType;
  title: string;                   // Reworded title
  description: string;             // Reworded description
  createdBy: string;
  createdAt: Date;
  createdFor?: string;             // Session or context this was created for
  isCanonical: boolean;            // Is this the "primary" frame?
  /** True when this frame represents the user's original wording (e.g. from LLM "write as multiple types") */
  isOriginal?: boolean;
  /** Altitude scoping answers for this frame (timeHorizon, workSize, impactSurface) */
  altitudeScopingInputs?: Record<string, string>;
  /** Computed altitude level (1-5) for this frame */
  altitudeLevel?: string;
}

// ============================================================================
// ITEM RELATIONS (Lightweight Links)
// ============================================================================

/**
 * Relation type between items
 *
 * @deprecated Phase 1 legacy type. Do not extend.
 * Use canonical relationship types from `itemRelationships.ts` for new work.
 */
export type ItemRelationType = 'duplicate' | 'variant' | 'related' | 'parent' | 'child';

/**
 * Relation status
 *
 * @deprecated Phase 1 legacy type. Do not extend.
 * Use canonical relationship status types from `itemRelationships.ts` for new work.
 */
export type ItemRelationStatus = 'proposed' | 'confirmed' | 'rejected';

/**
 * Lightweight item-to-item relationship
 * For tracking duplicates, variants, and related items
 *
 * @deprecated Phase 1 legacy interface. Do not extend.
 * Use canonical relationship records from `itemRelationships.ts` for new work.
 */
export interface ItemRelation {
  id: string;
  sourceItemId: string;
  targetItemId: string;
  relationType: ItemRelationType;
  
  // For duplicates: which is canonical?
  canonicalItemId?: string;
  
  // For variants: what dimension differs?
  variantContext?: {
    dimension: string;      // "platform", "region", "segment"
    sourceValue: string;    // "iOS"
    targetValue: string;    // "Android"
  };
  
  // For related: what's the relationship nature?
  relatedContext?: {
    relationshipNature: string;  // "same area", "prerequisite", "alternative"
  };
  
  // Provenance
  createdBy: string;
  createdAt: Date;
  confidence?: number;       // If AI-suggested (0-1)
  status: ItemRelationStatus;
}

// ============================================================================
// DEMAND CLUSTERS (First-Class Aggregation)
// ============================================================================

/**
 * Demand cluster status
 */
export type DemandClusterStatus = 'active' | 'addressed' | 'declined' | 'deferred';

/**
 * Contextual representative for a demand cluster
 * Different sessions may use different items to represent the same demand
 */
export interface ContextualRepresentative {
  context: string;           // "Q2 Planning", "Engineering Backlog"
  itemId: string;            // Which item represents here
  sessionId?: string;        // If tied to a specific session
}

/**
 * Demand cluster - first-class entity that aggregates related requests
 * Different from ItemRelation: this is an entity, not just a link
 *
 * @deprecated Transitional read-model concept. Do not extend as the canonical
 * relationship model moves to group-native and edge-native records.
 */
export interface DemandCluster {
  id: string;
  name: string;
  description: string;
  
  // Members
  memberItemIds: string[];
  representativeItemId?: string;
  contextualRepresentatives?: ContextualRepresentative[];
  
  // Signal strength
  requestCount: number;
  uniqueRequesters: number;
  earliestRequest: Date;
  latestRequest: Date;
  urgencyScore: number;      // Computed from frequency/recency
  
  // Status
  status: DemandClusterStatus;
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// ALTITUDE CONFIGURATION
// ============================================================================

/**
 * Altitude configuration - defines constraints and behaviors for each altitude level
 */
export interface AltitudeConfig {
  level: number;                   // 0-5
  name: ItemAltitude;              // 'task' | 'story' | etc.
  displayName: string;             // "Task", "Story", etc.
  
  // Time horizon constraints (in days)
  timeHorizon: {
    min: number;                   // Minimum days
    max: number;                   // Maximum days
  };
  
  // Structural constraints
  allowedChildAltitudes: ItemAltitude[];  // Must be exactly 1 level below
  requiresParent: boolean;         // Tasks don't require parent; Outcomes don't have parent
  
  // Language hints for validation
  languagePatterns: {
    preferred: string[];           // "Increase...", "Reduce...", "Enable..."
    discouraged: string[];         // "Build...", "Fix...", "Update..."
  };
}

/**
 * Standard altitude configurations
 */
export const ALTITUDE_CONFIGS: Record<ItemAltitude, AltitudeConfig> = {
  outcome: {
    level: 5,
    name: 'outcome',
    displayName: 'Outcome',
    timeHorizon: { min: 180, max: 730 },  // 6 months to 2 years
    allowedChildAltitudes: ['initiative'],
    requiresParent: false,
    languagePatterns: {
      preferred: ['Become...', 'Achieve...', 'Establish...', 'Increase...', 'Reduce...'],
      discouraged: ['Build...', 'Create...', 'Implement...', 'Fix...']
    }
  },
  initiative: {
    level: 4,
    name: 'initiative',
    displayName: 'Initiative',
    timeHorizon: { min: 90, max: 365 },  // Quarter to year
    allowedChildAltitudes: ['epic'],
    requiresParent: false,
    languagePatterns: {
      preferred: ['Improve...', 'Transform...', 'Enable...', 'Launch...'],
      discouraged: ['Code...', 'Test...', 'Debug...']
    }
  },
  epic: {
    level: 3,
    name: 'epic',
    displayName: 'Epic',
    timeHorizon: { min: 30, max: 90 },  // 1-3 months
    allowedChildAltitudes: ['feature'],
    requiresParent: false,
    languagePatterns: {
      preferred: ['Deliver...', 'Complete...', 'Provide...'],
      discouraged: ['Fix bug...', 'Update config...']
    }
  },
  feature: {
    level: 2,
    name: 'feature',
    displayName: 'Feature',
    timeHorizon: { min: 14, max: 42 },  // 2-6 weeks
    allowedChildAltitudes: ['story'],
    requiresParent: false,
    languagePatterns: {
      preferred: ['Add...', 'Support...', 'Allow...', 'Enable...'],
      discouraged: ['Refactor...', 'Migrate...']
    }
  },
  story: {
    level: 1,
    name: 'story',
    displayName: 'Story',
    timeHorizon: { min: 1, max: 14 },  // 1 day to 2 weeks
    allowedChildAltitudes: ['task'],
    requiresParent: false,
    languagePatterns: {
      preferred: ['As a...', 'User can...', 'System should...'],
      discouraged: ['Optimize...', 'Refactor...']
    }
  },
  task: {
    level: 0,
    name: 'task',
    displayName: 'Task',
    timeHorizon: { min: 0, max: 5 },  // Same day to 5 days
    allowedChildAltitudes: [],  // Atomic - no children
    requiresParent: false,      // Can exist independently
    languagePatterns: {
      preferred: ['Update...', 'Fix...', 'Add...', 'Remove...', 'Configure...'],
      discouraged: ['Improve...', 'Enhance...', 'Optimize...']  // Too vague for task
    }
  }
};

// ============================================================================
// SESSION CRITERIA MODEL
// ============================================================================

/**
 * Scoring method for a session
 */
export type ScoringMethod = 'weighted_sum' | 'rank_aggregation' | 'pairwise';

/**
 * Criterion direction - higher or lower is better
 */
export type CriterionDirection = 'higher_better' | 'lower_better';

/**
 * Session criterion - an attribute selected for use in a session
 */
export interface SessionCriterion {
  attributeKey: string;
  weight: number;              // 0-100, must sum to 100
  required: boolean;           // Must have value to be eligible
  direction: CriterionDirection;
}

/**
 * Session criteria model - complete configuration of criteria for a session
 */
export interface SessionCriteriaModel {
  id: string;
  sessionId: string;
  criteria: SessionCriterion[];
  scoringMethod: ScoringMethod;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// PRIORITIZATION OUTPUT
// ============================================================================

/**
 * Calculated ranking for a candidate
 */
export interface CalculatedRanking {
  itemId: string;
  score: number;
  rank: number;
  breakdown: {
    criterionId: string;
    value: number;
    weight: number;
    contribution: number;
  }[];
}

/**
 * Ratified ranking (after leadership adjustment)
 */
export interface RatifiedRanking {
  itemId: string;
  finalRank: number;
  adjustment: number;          // Difference from calculated rank (+ = moved up, - = moved down)
  adjustmentRationale?: string;
}

/**
 * Adjustment summary for tracking changes between calculated and ratified
 */
export interface AdjustmentSummary {
  totalAdjustments: number;
  itemsMoved: number;
  largestMove: {
    itemId: string;
    from: number;
    to: number;
  };
}

/**
 * Prioritization output - captures both calculated and ratified rankings
 * Preserves traceability between method output and leadership decisions
 */
export interface PrioritizationOutput {
  id: string;
  sessionId: string;
  
  // What the method produced
  calculatedRanking: CalculatedRanking[];
  
  // What leadership ratified
  ratifiedRanking: RatifiedRanking[];
  
  // Timestamps
  calculatedAt: Date;
  ratifiedAt?: Date;
  ratifiedBy?: string[];
  
  // Summary of adjustments
  adjustmentSummary: AdjustmentSummary;
}

// ============================================================================
// INTERPRETATION CHECK (Alignment Verification)
// ============================================================================

/**
 * Status for interpretation check
 */
export type InterpretationCheckStatus = 'collecting' | 'reviewing' | 'aligned' | 'divergent';

/**
 * Interpretation response from a participant
 */
export interface InterpretationResponse {
  userId: string;
  response: string;
  submittedAt: Date;
}

/**
 * Interpretation check - verifies alignment on item meaning
 * Surfaces "thought bubble" divergence
 */
export interface InterpretationCheck {
  id: string;
  itemId: string;
  question: string;            // "What outcome would this deliver?"
  responses: InterpretationResponse[];
  status: InterpretationCheckStatus;
  divergenceScore?: number;    // 0 = perfect alignment, 1 = total divergence
  facilitatorNote?: string;
}

// ============================================================================
// TYPE NORMALIZATION
// ============================================================================

/**
 * Status for type normalization task
 */
export type NormalizationStatus = 'pending' | 'converted' | 'skipped' | 'excluded';

/**
 * Type normalization task
 * Tracks items that need to be converted to the session's target type
 */
export interface TypeNormalizationTask {
  id: string;
  sessionId: string;
  itemId: string;
  
  // What we need
  targetType: ItemLanguageType;
  
  // Current state
  currentType: ItemLanguageType;
  needsConversion: boolean;
  
  // Conversion
  proposedFrame?: ItemFrame;
  status: NormalizationStatus;
  
  // Who did it
  convertedBy?: string;
  convertedAt?: Date;
}

// ============================================================================
// NORMALIZATION CHECKLIST
// ============================================================================

/**
 * Normalization checklist - tracks categorization completeness for a candidate
 */
export interface NormalizationChecklist {
  itemId: string;
  
  // Required categorizations
  languageType: { set: boolean; value?: ItemLanguageType };
  metaCategory: { set: boolean; value?: ItemMetaCategory };
  strategicBucket: { set: boolean; value?: StrategicBucket };
  altitude: { set: boolean; value?: ItemAltitude };
  
  // Computed
  isFullyNormalized: boolean;
  normalizationScore: number;  // 0-100%
  
  // Blocking
  blocksSessionEntry: boolean;
}

// ============================================================================
// CLARITY DOCUMENT
// ============================================================================

/**
 * Time horizon for focus
 */
export type TimeHorizon = 'immediate' | 'short' | 'medium' | 'long' | 'strategic';

/**
 * Clarity document status
 */
export type ClarityDocumentStatus = 'draft' | 'approved' | 'superseded';

/**
 * Clarity document - charter for a prioritization cycle
 * Captures the "why", "what", "who", and "how" of a prioritization effort
 */
export interface ClarityDocument {
  id: string;
  cycleId: string;
  
  // What we're deciding
  purpose: string;
  scope: {
    altitude: ItemAltitude;
    horizon: TimeHorizon;
    orgUnits: string[];
    themes?: string[];
  };
  
  // Why now
  trigger: string;  // "Q2 planning", "Budget request", "Strategy shift"
  
  // Who's involved
  sponsor: string;
  facilitator: string;
  participants: string[];
  
  // Criteria direction (not weights yet)
  criteriaCategories: string[];  // "Strategic fit", "Feasibility", "Risk"
  
  // Expected outputs
  expectedOutputs: string[];  // "Ranked list of Q2 initiatives", "Top 5 for resourcing"
  
  // Approval
  status: ClarityDocumentStatus;
  approvedBy?: string;
  approvedAt?: Date;
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// EXTEND ITEM WITH NEW FIELDS
// ============================================================================

/**
 * Extended item with clarification and framing support
 */
export interface ItemWithClarificationAndFrames extends Item {
  // Clarification workflow
  clarificationRequests?: ClarificationRequest[];
  hasPendingClarifications?: boolean;  // Computed
  
  // Item framing
  frames?: ItemFrame[];
  canonicalFrameId?: string;
}

// ============================================================================
// DECISION RIGHTS (Core B Governance)
// ============================================================================

import type { ScopeKind } from './permissions';
import type { ActorRole } from './roles';

/**
 * Types of decisions that can be governed
 */
export type DecisionType = 
  | 'criteria_selection'    // Who can select which criteria to use
  | 'criteria_weights'      // Who can set criteria weights
  | 'value_acceptance'      // Who can accept proposed values
  | 'ranking_override'      // Who can override calculated rankings
  | 'cutline_position'      // Who can set the cut line
  | 'aggregation_method'    // Who can choose aggregation method
  | 'publish';              // Who can publish results

/**
 * A decision right grants specific roles the authority to make a type of decision
 * at a specific scope level
 */
export interface DecisionRight {
  id: string;
  decisionType: DecisionType;
  
  // Who can make this decision
  grantedToRoles: ActorRole[];
  
  // Where this right applies
  scopeKind: ScopeKind;
  scopeId: string;
  
  // Optional constraints
  requiresQuorum?: number;        // Minimum number of participants
  requiresConsensus?: boolean;    // All must agree
  escalationPath?: ActorRole[];   // Who to escalate to if deadlocked
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Default decision rights for common scenarios.
 * Decision rights are now capability-gated; grantedToRoles should reference
 * admin roles only. Domain capabilities (CRITERIA_SELECT, SESSION_RUN, etc.)
 * are assigned to users directly via role_permissions.
 */
export const DEFAULT_DECISION_RIGHTS: Partial<DecisionRight>[] = [
  {
    decisionType: 'criteria_selection',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'session',
  },
  {
    decisionType: 'criteria_weights',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'session',
  },
  {
    decisionType: 'value_acceptance',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'session',
  },
  {
    decisionType: 'ranking_override',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'process',
  },
  {
    decisionType: 'cutline_position',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'session',
  },
  {
    decisionType: 'aggregation_method',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'process',
  },
  {
    decisionType: 'publish',
    grantedToRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    scopeKind: 'process',
  },
];

// ============================================================================
// ATTRIBUTE OWNERSHIP (Core B Governance)
// ============================================================================

/**
 * AttributeGovernance - Defines who can define, propose, and approve attribute values
 * 
 * This is the enhanced governance model that includes:
 * - Who owns/defines the attribute
 * - Who can propose values
 * - Who can approve values
 * - At what scope the governance applies
 */
export interface AttributeGovernance {
  /** User ID of the attribute owner/definer */
  ownerId?: string;
  
  /** Roles that can propose values for this attribute */
  valueProposerRoles: ActorRole[];
  
  /** Roles that can approve/accept values for this attribute */
  valueApproverRoles: ActorRole[];
  
  /** Scope at which this governance applies */
  scope: 'global' | 'bucket' | 'session';
  
  /** Optional description of governance rules */
  governanceNotes?: string;
}

/**
 * Default attribute governance configuration
 */
export const DEFAULT_ATTRIBUTE_GOVERNANCE: AttributeGovernance = {
  valueProposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
  valueApproverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
  scope: 'global',
};

/**
 * Defines who can propose and approve values for a specific attribute
 * at a specific scope level
 */
export interface AttributeOwnership {
  id: string;
  attributeKey: string;
  
  // Where this ownership applies
  scopeKind: ScopeKind;
  scopeId: string;
  
  // Governance configuration
  governance?: AttributeGovernance;
  
  // Who can propose values for this attribute
  proposerRoles: ActorRole[];
  
  // Who can approve/accept proposed values
  approverRoles: ActorRole[];
  
  // Evidence requirements
  requiresEvidence: boolean;
  evidenceTypes?: ('link' | 'document' | 'calculation' | 'expert_opinion')[];
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Default attribute ownership mappings based on common attribute types
 */
export const DEFAULT_ATTRIBUTE_OWNERSHIP: Partial<AttributeOwnership>[] = [
  {
    attributeKey: 'effort',
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    approverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    requiresEvidence: false,
  },
  {
    attributeKey: 'impact',
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    approverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    requiresEvidence: false,
  },
  {
    attributeKey: 'risk',
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    approverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    requiresEvidence: false,
  },
  {
    attributeKey: 'cost',
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    approverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    requiresEvidence: true,
    evidenceTypes: ['calculation', 'document'],
  },
  {
    attributeKey: 'strategic_alignment',
    proposerRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    approverRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    requiresEvidence: false,
  },
];

/**
 * Check if a role can propose values for an attribute given ownership rules
 */
export function canProposeValue(
  role: ActorRole,
  attributeKey: string,
  ownerships: AttributeOwnership[]
): boolean {
  const ownership = ownerships.find(o => o.attributeKey === attributeKey);
  if (!ownership) {
    // No ownership defined - allow by default
    return true;
  }
  return ownership.proposerRoles.includes(role);
}

/**
 * Check if a role can approve values for an attribute given ownership rules
 */
export function canApproveValue(
  role: ActorRole,
  attributeKey: string,
  ownerships: AttributeOwnership[]
): boolean {
  const ownership = ownerships.find(o => o.attributeKey === attributeKey);
  if (!ownership) {
    // No ownership defined - allow by default
    return true;
  }
  return ownership.approverRoles.includes(role);
}

// Backward-compat aliases (focus list → item pool terminology)
export type FocusList = ItemPool;
export type FocusListStatus = ItemPoolStatus;
export type FocusMap = ItemPoolMap;
export const canTransitionFocusList = canTransitionItemPool;

/**
 * @deprecated Use Item. "Candidate" is a deprecated term per the glossary.
 * Kept as a type alias so existing imports continue to compile during the sweep.
 */
export type Candidate = Item;

/** @deprecated Use Item. */
export type CandidateMetaCategory = ItemMetaCategory;
